// FILE: /src/Components/Redirectpage/Youtube.js (FULLY UPDATED)
import React, { useEffect, useState, useMemo } from "react";
import { Spin, message, Result, Button, Checkbox } from 'antd';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as authServices from '../../services/authConnect';
import { handleAuthSuccess } from "../../store/Slices/GroupSlice";

// --- START: NEW COMPONENT ---
const ChannelSelectionView = ({ channels, onFinalize, onCancel }) => {
  const [selectedItems, setSelectedItems] = useState([]);

  const getChannelThumbnail = (channel) =>
    channel?.snippet?.thumbnails?.high?.url ||
    channel?.snippet?.thumbnails?.medium?.url ||
    channel?.snippet?.thumbnails?.default?.url ||
    '';

  const ChannelAvatar = ({ channel }) => {
    const [failed, setFailed] = useState(false);
    const src = getChannelThumbnail(channel);
    const title = channel?.snippet?.title || 'YouTube Channel';

    if (!src || failed) {
      return (
        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
          {title.charAt(0).toUpperCase()}
        </div>
      );
    }

    return (
      <img
        src={src}
        className="w-10 h-10 rounded-full object-cover"
        alt={title}
        onError={() => setFailed(true)}
      />
    );
  };

  const handleItemSelect = (item, isChecked) => {
    setSelectedItems(prev =>
      isChecked ? [...prev, item] : prev.filter(i => i.id !== item.id)
    );
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-lg bg-white p-6 rounded-lg shadow-lg">
        <h3 className="text-xl font-semibold mb-2">Select YouTube Channels</h3>
        <p className="text-gray-500 mb-4">Choose which channels you'd like to connect to your brand.</p>
        <div className="h-72 overflow-y-auto border rounded-lg p-2">
          {channels.map(channel => (
            <div key={channel.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
              <div className="flex items-center gap-3">
                <ChannelAvatar channel={channel} />
                <div>
                  <span className="font-semibold">{channel.snippet.title}</span>
                  <p className="text-xs text-gray-500">
                    {channel.statistics.subscriberCount} subscribers · {channel.statistics.videoCount} videos
                  </p>
                </div>
              </div>
              <Checkbox
                onChange={(e) => handleItemSelect(channel, e.target.checked)}
                checked={selectedItems.some(i => i.id === channel.id)}
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button key="back" onClick={onCancel}>Cancel</Button>
          <Button key="submit" type="primary" onClick={() => onFinalize(selectedItems)} disabled={selectedItems.length === 0}>
            Connect ({selectedItems.length})
          </Button>
        </div>
      </div>
    </div>
  );
};
// --- END: NEW COMPONENT ---

export default function Youtube() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');
  const [selectionData, setSelectionData] = useState(null);
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    const authStatus = params.get('status');
    const errorMessage = params.get('message');
    const channelsParam = params.get('channels');
    const stateToken = params.get('state');

    if (authStatus === 'success') {
      message.success("YouTube channel connected successfully!", 2.5)
        .then(() => {
          dispatch(handleAuthSuccess());
          navigate('/Dashboard/GroupsProfile');
        });
    } else if (authStatus === 'error') {
      setError(decodeURIComponent(errorMessage) || 'An unknown error occurred.');
      setStatus('error');
    } else if (authStatus === 'selection' && channelsParam && stateToken) {
      try {
        const decodedChannels = JSON.parse(decodeURIComponent(channelsParam));
        setSelectionData({ channels: decodedChannels, stateToken });
        setStatus('selecting');
      } catch (e) {
        setError('Could not read channel data from Google.');
        setStatus('error');
      }
    } else {
      setError('Invalid redirect. Please try connecting your account again.');
      setStatus('error');
    }
  }, [dispatch, navigate, params]);

  const handleFinalize = async (selectedItems) => {
    if (selectedItems.length === 0) {
      message.info("Please select at least one channel.");
      return;
    }

    setStatus('finalizing');
    try {
      await authServices.finalize('YOUTUBE', selectedItems, selectionData.stateToken);
      message.success(`${selectedItems.length} channel(s) connected successfully!`);
      dispatch(handleAuthSuccess());
      navigate('/Dashboard/GroupsProfile');
    } catch (err) {
      setError(err.message || "Failed to save selected channels.");
      setStatus('error');
    }
  };

  if (status === 'error') {
    return (
      <Result
        status="error"
        title="Connection Failed"
        subTitle={error}
        extra={[
          <Button type="primary" key="console" onClick={() => navigate('/Dashboard/GroupsProfile')}>
            Go back to Brands & Channels
          </Button>,
        ]}
      />
    );
  }

  if (status === 'selecting') {
    return (
      <ChannelSelectionView
        channels={selectionData.channels}
        onFinalize={handleFinalize}
        onCancel={() => navigate('/Dashboard/GroupsProfile')}
      />
    );
  }

  return (
    <div className="flex flex-col justify-center items-center h-screen">
      <Spin size="large" />
      <p className="mt-4 text-lg text-gray-600">Finalizing connection...</p>
    </div>
  );
}